import EditIcon from '@mui/icons-material/Edit';
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../../constants";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../../shared/Button/customOutlinedButton";
import { RootState } from "../../../../store/store";
import basestyle from "../../../CSS/Base.module.css";
import { default as CancelPopUp, default as SubmitPopUp } from "../../../PopUp/PopUp";
import { saveAutoDebitDetails } from "../../services/AutoDebit/AutoDebitServices";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../../StyledComponents/Container";
import { notifyUser } from "../../Utils/notifyUser";
import CommonComponet from "./CommonCompoent";

function convertToISOString(inputDate: string): string {
    const [month, day, year] = inputDate.split("/").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 8, 0, 0)); // 8:00 AM UTC
    return date.toISOString();
}

const AutoDebitReviewAndSubmit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const formData = location.state;
    const userInfo = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;

    const [open, setOpen] = useState(false);
    const [openPopUp, setOpenPopUp] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleBackClick = () => {
        navigate("/autoDebitLoanDetails", { state: { submitData: formData } });
    }
    const handleCancel = () => {
        setOpenPopUp(true);
    };
    const handlePopUpClose = () => {
        setOpenPopUp(false);
    };
    function handleConfirm() {
        navigate("/autoDebitListing");
    }

    const handleSubmitClose = () => {
        setOpen(false);
    };
    const saveSubmitChanges = () => {
        setOpen(true);
    };

    const handleSave = async () => {
        // CHANGED: Added a guard clause to prevent double-submission.
        if (isLoading) {
            return;
        }

        setOpen(false);
        try {
            setIsLoading(true);
            const saveAutoDebitVo = {
                id: formData.autoDebitId,
                participantId: userInfo.defaultParticipantId,
                loanId: formData.loanNumber?.loanId,
                loanNumber: formData.loanNumber?.loanNumber,
                loanDescription: formData.description,
                sourceName: formData.fundType,
                entityId: formData.fundId,
                entityType: formData.fundType,
                paymentRecurring: formData.isRecurring ? constants.IS_Active_Yes : constants.IS_Active_No,
                frequency: formData.frequency?.value,
                paymentEndingDate: formData.endDate,
                amount: formData.amount?.value,
                autoDebitOption: formData.autoDebit?.value,
                paymentBeginningDate: convertToISOString(formData.beginningDate),
                status: constants.IS_Status_ACTIVE,
                isActive: constants.IS_ACTIVE_YES,
                version: formData.version,
                autoDebitScheduleDetails: formData.autoDebitScheduleDetails.length === 0 ?
                    [
                        {
                            status: constants.IS_Status_OPEN,
                            scheduledDate: convertToISOString(formData.beginningDate)
                        }
                    ]
                    : formData.autoDebitScheduleDetails
            };
            const response = await saveAutoDebitDetails(saveAutoDebitVo);

            if (response.status === constants.STATUS_CODES.CREATED) {
                navigate("/autoDebitListing", { state: { message: response.data.message } });
            } else {
                notifyUser(response.response.data.message, "error", translate("reviewAndSubmit"));
                // Note: No need to setOpen(false) here, it was already done before the try block
            }
        } catch (error: any) {
            notifyUser(error.response.response.data.message ?? translate("unexpectedError:"), "error", translate("reviewAndSubmit"));

        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>
                        {translate('reviewAndSubmit')} &nbsp;<EditIcon sx={{ cursor: 'pointer' }} onClick={handleBackClick} />
                    </SectionTitle>
                    <Box sx={{ width: "100%", px: 2 }}>
                        <CommonComponet data={formData} />
                        <Box className={basestyle.buttoncontainer} >
                            {/* CHANGED: Added the disabled prop based on the isLoading state */}
                            <ContainedButton onClick={saveSubmitChanges} disabled={isLoading}>
                                {translate('submit')}
                            </ContainedButton>
                            <ContainedButton onClick={handleBackClick}> {translate('back')}  </ContainedButton>
                            <OutlinedButton onClick={handleCancel}> {translate('cancel')}  </OutlinedButton>
                        </Box>
                    </Box>
                    <Backdrop
                        sx={(theme) => ({
                            color: "#fff",
                            zIndex: theme.zIndex.drawer + 1,
                            display: "flex",
                            flexDirection: "column",
                        })}
                        open={isLoading}
                    >
                        <CircularProgress color="inherit" />
                        <Typography color="white">Please wait...</Typography>
                    </Backdrop>

                    <SubmitPopUp
                        open={open}
                        popupTitle={translate('confirmSubmit')}
                        handleClose={handleSubmitClose}
                        saveChanges={() => handleSave()}
                        message={translate('doYouWantToSubmitChanges?')}
                    />

                    <CancelPopUp
                        open={openPopUp}
                        popupTitle={translate('confirmCancel')}
                        handleClose={handlePopUpClose}
                        saveChanges={() => handleConfirm()}
                        message={translate('doYouWantToCancelChanges?')}
                    />
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    )
}
export default AutoDebitReviewAndSubmit;